import { useState, useEffect } from 'react';
import axios from '../config/axios';

interface TimeUsage {
  daily_minutes: number;
  total_minutes: number;
}

interface RecordingCounts {
  daily_count: number;
  total_count: number;
}

interface Limits {
  daily_limit: number | null;
  total_limit: number | null;
  max_duration: number | null;
  unlimited: boolean;
}

interface RecordingTimeData {
  time_usage: TimeUsage;
  recording_counts: RecordingCounts;
  limits: Limits;
}

// API Response structure (what we actually get from the backend)
interface ApiRecordingTimeResponse {
  total_minutes: number;
  daily_count: number;
  total_count: number;
  recording_limits: {
    daily_limit: number;
    total_limit: number;
    max_duration: number;
    daily_used: number;
    total_used: number;
    unlimited: boolean;
    is_paid: boolean;
    subscription_status: string;
  };
}

export const useRecordingTime = () => {
  const [data, setData] = useState<RecordingTimeData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRecordingTime = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('/api/scribing/user-profile/me/recording-time');
      
      // Transform API response to expected format
      const apiData: ApiRecordingTimeResponse = response.data;
      const transformedData: RecordingTimeData = {
        time_usage: {
          daily_minutes: 0, // API doesn't provide daily minutes, so we'll use 0
          total_minutes: apiData.total_minutes || 0
        },
        recording_counts: {
          daily_count: apiData.daily_count || 0,
          total_count: apiData.total_count || 0
        },
        limits: {
          daily_limit: apiData.recording_limits?.daily_limit || null,
          total_limit: apiData.recording_limits?.total_limit || null,
          max_duration: apiData.recording_limits?.max_duration || null,
          unlimited: apiData.recording_limits?.unlimited || false
        }
      };
      
      setData(transformedData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch recording time data');
      console.error('Error fetching recording time:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRecordingTime();
  }, []);

  const checkRecordingLimits = (duration: number): { canRecord: boolean; message: string } => {
    if (!data || !data.limits) {
      return { canRecord: false, message: 'Unable to check limits' };
    }

    const { time_usage, recording_counts, limits } = data;

    // If unlimited, allow recording
    if (limits?.unlimited) {
      return { canRecord: true, message: '' };
    }

    // Check daily recording count limit
    // null = unlimited, 0 = no recordings allowed, positive number = limit
    if (limits?.daily_limit !== null) {
      if (limits.daily_limit === 0) {
        return {
          canRecord: false,
          message: `Daily recording limit is set to 0. Please upgrade your plan to start recording.`
        };
      }
      if (limits.daily_limit > 0 && recording_counts?.daily_count >= limits.daily_limit) {
        return {
          canRecord: false,
          message: `You've reached your daily recording limit of ${limits.daily_limit} recordings. Please upgrade your plan for more recordings.`
        };
      }
    }

    // Check total recording count limit
    // null = unlimited, 0 = no recordings allowed, positive number = limit
    if (limits?.total_limit !== null) {
      if (limits.total_limit === 0) {
        return {
          canRecord: false,
          message: `Total recording limit is set to 0. Please upgrade your plan to start recording.`
        };
      }
      if (limits.total_limit > 0 && recording_counts?.total_count >= limits.total_limit) {
        return {
          canRecord: false,
          message: `You've reached your total recording limit of ${limits.total_limit} recordings. Please upgrade your plan for more recordings.`
        };
      }
    }

    // Check max duration limit
    // null = unlimited, 0 = no recording allowed, positive number = limit in minutes
    if (limits?.max_duration !== null) {
      if (limits.max_duration === 0) {
        return {
          canRecord: false,
          message: `Maximum recording duration is set to 0 minutes. Please upgrade your plan to start recording.`
        };
      }
      if (limits.max_duration > 0 && duration > limits.max_duration) {
        return {
          canRecord: false,
          message: `Recording duration exceeds the maximum allowed duration of ${limits.max_duration} minutes. Please upgrade your plan for longer recordings.`
        };
      }
    }

    // Check if we're using daily_limit for time (this seems to be a legacy issue in the original code)
    // The original code was checking daily_limit against time_usage.daily_minutes, which seems incorrect
    // We'll keep this for backward compatibility but it should be reviewed
    // if (limits?.daily_limit !== null && (time_usage?.daily_minutes + duration) > limits.daily_limit) {
    //   return {
    //     canRecord: false,
    //     message: `This recording would exceed your daily time limit. Please upgrade your plan for more recording time.`
    //   };
    // }

    return { canRecord: true, message: '' };
  };

  return {
    data,
    isLoading,
    error,
    fetchRecordingTime,
    checkRecordingLimits
  };
}; 